import React, { useEffect } from 'react';
import AppSetupModal, {
  AppSetupModalProps,
} from '~/components/template/AppSetupModal';
import { useStartNvmOauthMutation } from '~/graphql/types';
import useAddToast from '~/hooks/useAddToast';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useErrorReporter from '~/hooks/useErrorReporter';
import useNativePopup from '~/hooks/useNativePopup';
import { nvmOAuthSuccessPath } from '~/components/page/Apps/BBWaardecheck/components/NvmOAuthSuccess';
import formatToastMessage from '~/util/formatToastMessage';
import getEnablementFlow from '~/components/organism/Wizard/utils/getEnablementFlow';
import useWizard from '~/hooks/useWizard';
import usePrevious from '~/hooks/usePrevious';
import getNvmAppType from '../../../../utils/getNvmAppType';
import isNvmApp from '../../../../utils/isNvmApp';
import useContextAwareToggle from '~/hooks/useContextAwareToggle';

const text = {
  errorMessage:
    'Er is iets misgegaan bij het starten van NVM login. Probeer het later nog eens.',
};

type Props = AppSetupModalProps;

const AppSetupModalWithOAuthFlow: React.FCC<Props> = ({ ...rest }) => {
  const errorReporter = useErrorReporter();
  const account = useCurrentAccount();
  const addToast = useAddToast();
  const app = rest.app;
  const { appType, appStatus, name } = app;
  const wizardApi = useWizard();
  const { value: isContextAwareEnabled } = useContextAwareToggle();

  const [startNvmOAuth, { error }] = useStartNvmOauthMutation();

  const { open } = useNativePopup({
    onClose: () => {
      // refetch app statuses so that the app modal gets updated
      rest.refetch();
    },
  });

  const onOAuthCall = async ({ onError }) => {
    // This will be replaced with startOAuthForAppType mutation
    const { data } = await startNvmOAuth({
      variables: {
        accountId: account.id,
        participatingOffices: 1,
        // Redirect back into the setup modal so the user can click "finish installation"
        callbackUrl: `${origin}/-/apps/${nvmOAuthSuccessPath}`,
        appType: getNvmAppType(appType),
      },
    });

    if (error || !data) {
      errorReporter.captureException(
        new Error(JSON.stringify(error || 'No Data')),
        'fatal',
      );

      addToast([formatToastMessage(text.errorMessage, 'danger')]);

      return onError();
    }

    open({ url: data.startNVMOauth.redirectUrl });
  };

  const prevEnabled = usePrevious(appStatus.enabled);
  const justEnabled = prevEnabled !== appStatus.enabled;
  const setupCompleted =
    'metadata' in appStatus && appStatus.metadata != null
      ? appStatus.metadata.setup.completed
      : appStatus.enabled;

  const enablementFlow = getEnablementFlow({
    app,
    hasFlows: rest.hasFlows,
    isContextAwareEnabled,
  });
  const enabled = appStatus.enabled;
  const isNvmAppType = isNvmApp(appType);

  const shouldShowWizard =
    justEnabled && enabled && enablementFlow && !setupCompleted && isNvmAppType;

  useEffect(() => {
    if (shouldShowWizard) {
      wizardApi.show({
        header: name,
        ...enablementFlow,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowWizard, app, enablementFlow]);

  return <AppSetupModal {...rest} onOAuthCall={onOAuthCall} />;
};

export default AppSetupModalWithOAuthFlow;
